import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { hrmApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'cycles' | 'my_goals' | 'my_reviews';

/** FR-HR-05 — Performance */
export function PerformancePage() {
  const [tab, setTab] = useState<Tab>('cycles');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'cycles',     label: 'Cycles' },
    { key: 'my_goals',   label: 'My goals' },
    { key: 'my_reviews', label: 'My reviews' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Performance"
        subtitle="Manage review cycles, track your goals, and complete self and manager reviews."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'cycles' && <CyclesTab />}
      {tab === 'my_goals' && <MyGoalsTab />}
      {tab === 'my_reviews' && <MyReviewsTab />}
    </div>
  );
}

function CyclesTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['performance-cycles'],
    queryFn: () => hrmApi.listCycles(),
  });

  const transitionMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      hrmApi.updateCycle(id, { status }),
    onSuccess: (_, vars) => {
      toast.success(`Cycle ${vars.status}`);
      qc.invalidateQueries({ queryKey: ['performance-cycles'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteCycleMut = useMutation({
    mutationFn: (id: string) => hrmApi.deleteCycle(id),
    onSuccess: (_, id) => {
      toast.success('Cycle deleted');
      if (selected === id) setSelected(null);
      qc.invalidateQueries({ queryKey: ['performance-cycles'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let open = 0, draft = 0, closed = 0, totalGoals = 0;
    for (const c of rows) {
      if (c.status === 'open') open++;
      else if (c.status === 'draft') draft++;
      else if (c.status === 'closed') closed++;
      totalGoals += c.goal_count ?? 0;
    }
    return { total: rows.length, open, draft, closed, totalGoals };
  })();

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Cycles" value={kpis.total} />
        <KpiTile label="Open" value={kpis.open} accent={kpis.open > 0 ? 'sage' : 'default'} />
        <KpiTile label="Draft" value={kpis.draft} accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Goals across cycles" value={kpis.totalGoals} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New cycle</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No cycles yet"
              hint="Create a performance cycle to start tracking goals and reviews for a period."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New cycle</button>
              }
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th className="text-right">Goals</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(q.data ?? []).map((c: any) => (
                      <tr key={c.id} onClick={() => setSelected(c.id)}
                          className={`cursor-pointer ${selected === c.id ? '!bg-canvas-subtle' : ''}`}>
                        <td className="font-mono text-xs font-semibold text-ink-900">{c.code}</td>
                        <td className="text-sm text-ink-900">{c.name}</td>
                        <td className="text-xs text-ink-600 font-mono tabular-nums">
                          {new Date(c.starts_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          <span className="mx-1 text-ink-400">→</span>
                          {new Date(c.ends_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </td>
                        <td><CycleStatusBadge status={c.status} /></td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">{c.goal_count}</td>
                        <td className="text-right">
                          <button
                            className="text-ink-400 hover:text-danger px-1.5 py-1 rounded transition-colors"
                            title="Delete cycle"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete cycle ${c.code}? This cannot be undone.`)) deleteCycleMut.mutate(c.id);
                            }}
                          >✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card p-5 min-h-[400px]">
              {!selected ? (
                <EmptyState
                  message="Select a cycle"
                  hint="Click a row on the left to view cycle details and manage goals."
                />
              ) : (
                <CycleDetail cycleId={selected}
                  onTransition={(status) => transitionMut.mutate({ id: selected, status })}
                  onGoalChanged={() => qc.invalidateQueries({ queryKey: ['performance-cycles'] })} />
              )}
            </div>
          </div>
        )}

      {showCreate && <CreateCycleModal onClose={() => setShowCreate(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['performance-cycles'] }); setShowCreate(false); setSelected(id); }} />}
    </div>
  );
}

function CycleStatusBadge({ status }: { status: string }) {
  const cls = {
    draft:  'bg-canvas-subtle text-ink-700 ring-ink-200',
    open:   'bg-sage-50 text-sage-700 ring-sage-200',
    closed: 'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function CycleDetail({ cycleId, onTransition, onGoalChanged }:
    { cycleId: string; onTransition: (s: string) => void; onGoalChanged: () => void }) {
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showCloseCycle, setShowCloseCycle] = useState(false);

  const cyclesQ = useQuery({ queryKey: ['performance-cycles'], queryFn: () => hrmApi.listCycles() });
  const cycle = cyclesQ.data?.find((c: any) => c.id === cycleId);

  const goalsQ = useQuery({
    queryKey: ['performance-goals', cycleId],
    queryFn: () => hrmApi.listGoals({ cycleId }),
  });

  if (!cycle) return <EmptyState message="Cycle not found." />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{cycle.code}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{cycle.name}</div>
          {cycle.description && (
            <div className="text-sm text-ink-500 mt-1 leading-relaxed">{cycle.description}</div>
          )}
          <div className="text-xs text-ink-500 mt-1 font-mono tabular-nums">
            {new Date(cycle.starts_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            <span className="mx-1.5 text-ink-300">→</span>
            {new Date(cycle.ends_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {cycle.review_due_by && (
            <div className="text-xs text-ink-500 mt-0.5">
              Reviews due by {new Date(cycle.review_due_by).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
        </div>
        <CycleStatusBadge status={cycle.status} />
      </div>

      {/* Workflow actions */}
      <div className="mb-5 flex gap-2 flex-wrap">
        {cycle.status === 'draft' && (
          <button className="btn-primary" onClick={() => onTransition('open')}>Open cycle</button>
        )}
        {cycle.status === 'open' && (
          <>
            <button className="btn-secondary" onClick={() => setShowAddGoal(true)}>+ Add goal</button>
            <button className="btn-secondary" onClick={() => setShowCloseCycle(true)}>
              Close cycle
            </button>
          </>
        )}
      </div>

      {/* Goals */}
      <div className="eyebrow text-ink-500 mb-2">Goals ({goalsQ.data?.length ?? 0})</div>
      {goalsQ.isLoading ? <div className="card"><LoadingState rows={3} /></div> :
       goalsQ.error ? <ErrorState error={goalsQ.error} /> :
       !goalsQ.data?.length ? (
        <div className="card">
          <EmptyState
            message="No goals yet"
            hint={cycle.status === 'open' ? 'Add the first goal to get started.' : 'Open the cycle to start adding goals.'}
          />
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
          {(goalsQ.data ?? []).map((g: any) => (
            <GoalCard key={g.id} goal={g} onChanged={() => {
              goalsQ.refetch();
              onGoalChanged();
            }} />
          ))}
        </div>
      )}

      {showAddGoal && <AddGoalModal cycleId={cycleId} onClose={() => setShowAddGoal(false)}
        onCreated={() => { goalsQ.refetch(); setShowAddGoal(false); }} />}

      {showCloseCycle && (
        <ConfirmCloseCycleDialog
          cycleName={cycle.name}
          onClose={() => setShowCloseCycle(false)}
          onConfirm={() => { setShowCloseCycle(false); onTransition('closed'); }}
        />
      )}
    </div>
  );
}

function ConfirmCloseCycleDialog({
  cycleName, onClose, onConfirm,
}: { cycleName: string; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Close cycle</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Close this cycle?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Close <span className="font-semibold text-ink-900">{cycleName}</span>? No more goals
          can be added, no more reviews can be submitted, and existing goals can no longer be
          rated. This is final.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep it open</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700" onClick={onConfirm}>
          Close cycle
        </button>
      </div>
    </Modal>
  );
}

function GoalCard({ goal, onChanged }: { goal: any; onChanged: () => void }) {
  const [rating, setRating] = useState<string>('');

  const rateMut = useMutation({
    mutationFn: () => hrmApi.rateGoal(goal.id, { completionRating: parseInt(rating, 10) }),
    onSuccess: () => { toast.success(`Rated ${rating}/5`); onChanged(); setRating(''); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="bg-canvas-elevated border border-ink-100 rounded-xl p-3 text-xs">
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-ink-900">{goal.title}</div>
          <div className="text-2xs text-ink-500 mt-0.5">
            {goal.employee_name}
            {goal.category && <span className="mx-1 text-ink-300">·</span>}
            {goal.category && <span className="uppercase tracking-wider">{goal.category}</span>}
            {goal.weight_percent > 0 && (
              <>
                <span className="mx-1 text-ink-300">·</span>
                weight {goal.weight_percent}%
              </>
            )}
          </div>
          {goal.description && <div className="text-ink-700 mt-1.5 leading-relaxed">{goal.description}</div>}
          {goal.target_metric && (
            <div className="text-ink-600 mt-1.5">
              Target: <span className="font-mono">{goal.target_metric}</span>
              {goal.target_value && <span className="ml-1 font-mono font-semibold">{goal.target_value}</span>}
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <GoalStatusBadge status={goal.status} />
          {goal.completion_rating && (
            <div className="font-display font-bold text-xl text-sage-700 mt-1.5 tabular-nums">
              {goal.completion_rating}<span className="text-sm text-ink-400">/5</span>
            </div>
          )}
        </div>
      </div>

      {!goal.completion_rating && goal.status === 'active' && (
        <div className="mt-3 flex gap-1.5 items-center pt-3 border-t border-ink-100">
          <select className="input text-xs py-1 flex-1" value={rating}
            onChange={(e) => setRating(e.target.value)}>
            <option value="">Rate 1–5…</option>
            <option value="1">1 — Below expectations</option>
            <option value="2">2 — Needs improvement</option>
            <option value="3">3 — Meets expectations</option>
            <option value="4">4 — Exceeds</option>
            <option value="5">5 — Outstanding</option>
          </select>
          <button className="btn-secondary text-xs py-1" disabled={!rating || rateMut.isPending}
            onClick={() => rateMut.mutate()}>
            {rateMut.isPending ? 'Saving…' : 'Rate'}
          </button>
        </div>
      )}
    </div>
  );
}

function GoalStatusBadge({ status }: { status: string }) {
  const cls = {
    active:    'bg-blue-50 text-blue-700 ring-blue-200',
    completed: 'bg-sage-50 text-sage-700 ring-sage-200',
    dropped:   'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function MyGoalsTab() {
  const q = useQuery({
    queryKey: ['performance-goals-mine'],
    queryFn: () => hrmApi.listGoals(),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div className="space-y-2">
      {!q.data?.length ? (
        <div className="card p-4"><EmptyState message="No goals assigned to you yet." /></div>
      ) : (q.data ?? []).map((g: any) => (
        <div key={g.id} className="card p-3">
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-ink-900">{g.title}</div>
              <div className="text-2xs text-ink-500 mt-0.5">
                {g.cycle_name} <span className="font-mono">({g.cycle_code})</span>
                {g.category && <><span className="mx-1 text-ink-300">·</span><span className="uppercase tracking-wider">{g.category}</span></>}
                {g.weight_percent > 0 && <><span className="mx-1 text-ink-300">·</span>weight {g.weight_percent}%</>}
                {g.due_date && <><span className="mx-1 text-ink-300">·</span>due {new Date(g.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>}
              </div>
              {g.description && <div className="text-sm text-ink-700 mt-1.5 leading-relaxed">{g.description}</div>}
              {g.target_metric && (
                <div className="text-xs text-ink-600 mt-1.5">
                  Target: <span className="font-mono">{g.target_metric}</span>
                  {g.target_value && <span className="ml-1 font-mono font-semibold">{g.target_value}</span>}
                </div>
              )}
              {g.completion_notes && (
                <div className="text-xs text-ink-600 mt-2 italic leading-relaxed">Notes: {g.completion_notes}</div>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <GoalStatusBadge status={g.status} />
              {g.completion_rating && (
                <div className="font-display font-bold text-xl text-sage-700 mt-1.5 tabular-nums">
                  {g.completion_rating}<span className="text-sm text-ink-400">/5</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyReviewsTab() {
  const [showSubmit, setShowSubmit] = useState(false);

  const q = useQuery({
    queryKey: ['performance-reviews-mine'],
    queryFn: () => hrmApi.listReviews(),
  });
  const cyclesQ = useQuery({
    queryKey: ['performance-cycles-open'],
    queryFn: () => hrmApi.listCycles({ status: 'open' }),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" disabled={!cyclesQ.data?.length}
          onClick={() => setShowSubmit(true)}>
          Submit self-review
        </button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="space-y-2">
          {!q.data?.length ? (
            <div className="card p-4"><EmptyState message="No reviews yet." /></div>
          ) : (q.data ?? []).map((r: any) => (
            <div key={r.id} className="card p-4">
              <div className="flex justify-between items-start mb-3 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-700 ring-ink-200">
                      {r.reviewer_type}
                    </span>
                    <span className="font-display font-semibold text-ink-900">{r.cycle_name}</span>
                  </div>
                  <div className="text-2xs text-ink-500 mt-1">
                    {r.reviewer_name && <>by {r.reviewer_name}</>}
                    {r.submitted_at && (
                      <>
                        <span className="mx-1 text-ink-300">·</span>
                        submitted {new Date(r.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </>
                    )}
                  </div>
                </div>
                {r.overall_rating && (
                  <div className="text-right flex-shrink-0">
                    <div className="eyebrow text-ink-500 text-2xs">Overall</div>
                    <div className="font-display font-bold text-2xl text-sage-700 tabular-nums mt-0.5">
                      {r.overall_rating}<span className="text-base text-ink-400">/5</span>
                    </div>
                  </div>
                )}
              </div>
              {r.strengths && (
                <div className="text-sm mb-1.5">
                  <span className="font-semibold text-ink-900">Strengths:</span>
                  <span className="text-ink-700 ml-1">{r.strengths}</span>
                </div>
              )}
              {r.improvements && (
                <div className="text-sm mb-1.5">
                  <span className="font-semibold text-ink-900">Improvements:</span>
                  <span className="text-ink-700 ml-1">{r.improvements}</span>
                </div>
              )}
              {r.goals_for_next_cycle && (
                <div className="text-sm mb-1.5">
                  <span className="font-semibold text-ink-900">Next cycle goals:</span>
                  <span className="text-ink-700 ml-1">{r.goals_for_next_cycle}</span>
                </div>
              )}
              {r.comments && <div className="text-sm text-ink-600 italic leading-relaxed">{r.comments}</div>}
            </div>
          ))}
        </div>
      )}

      {showSubmit && cyclesQ.data?.length > 0 && (
        <SubmitReviewModal cycles={cyclesQ.data}
          onClose={() => setShowSubmit(false)}
          onSubmitted={() => { q.refetch(); setShowSubmit(false); }} />
      )}
    </div>
  );
}

function CreateCycleModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const in90 = (() => { const d = new Date(); d.setDate(d.getDate() + 90); return d.toISOString().slice(0, 10); })();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState(in90);
  const [reviewDueBy, setReviewDueBy] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createCycle({
      code: code.trim() ? code.toUpperCase() : undefined, name,
      description: description || undefined,
      startsOn, endsOn,
      reviewDueBy: reviewDueBy || undefined,
    }),
    onSuccess: (d) => { toast.success(`Created ${d.code}`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">People · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New performance cycle</h2>
        <p className="text-sm text-ink-500 mt-1">
          Draft a review period. Open it when ready to start collecting goals and self-reviews.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3">
        <div>
          <label className="label">Code <span className="text-ink-400 font-normal">(auto if blank)</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto (PERF-0001)" />
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Q2 2026 review" />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Context for the team" />
        </div>
        <div>
          <label className="label">Starts</label>
          <input type="date" className="input" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </div>
        <div>
          <label className="label">Ends</label>
          <input type="date" className="input" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label">Reviews due by <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={reviewDueBy}
            onChange={(e) => setReviewDueBy(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </Modal>
  );
}

function AddGoalModal({ cycleId, onClose, onCreated }:
    { cycleId: string; onClose: () => void; onCreated: () => void }) {
  const empQ = useQuery({ queryKey: ['employees'], queryFn: () => hrmApi.listEmployees() });

  const [employeeId, setEmployeeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'skills'|'results'|'behaviors'|'development'>('results');
  const [weightPercent, setWeightPercent] = useState(0);
  const [targetMetric, setTargetMetric] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [dueDate, setDueDate] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createGoal({
      cycleId, employeeId, title,
      description: description || undefined,
      category,
      weightPercent,
      targetMetric: targetMetric || undefined,
      targetValue: targetValue || undefined,
      dueDate: dueDate || undefined,
    }),
    onSuccess: () => { toast.success('Goal added'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · Goal</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Add goal</h2>
        <p className="text-sm text-ink-500 mt-1">
          Create a SMART goal for an employee in this cycle. They can be rated 1–5 once the cycle ends.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0">
        <div className="col-span-2">
          <label className="label">Employee <span className="text-danger">*</span></label>
          <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {empQ.data?.map((e: any) => (
              <option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Ship Q2 roadmap on time" />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Context for how success will be measured" />
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value as any)}>
            <option value="results">Results</option>
            <option value="skills">Skills</option>
            <option value="behaviors">Behaviors</option>
            <option value="development">Development</option>
          </select>
        </div>
        <div>
          <label className="label">Weight %</label>
          <input type="number" min={0} max={100}
            className="input tabular-nums" value={weightPercent}
            onChange={(e) => setWeightPercent(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Target metric <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={targetMetric}
            onChange={(e) => setTargetMetric(e.target.value)} placeholder="Customer NPS" />
        </div>
        <div>
          <label className="label">Target value <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)} placeholder=">= 8.0" />
        </div>
        <div className="col-span-2">
          <label className="label">Due date <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!employeeId || !title || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Adding…' : 'Add goal'}
        </button>
      </div>
    </Modal>
  );
}

function SubmitReviewModal({ cycles, onClose, onSubmitted }:
    { cycles: any[]; onClose: () => void; onSubmitted: () => void }) {
  const todayQ = useQuery({ queryKey: ['attendance-today'], queryFn: () => hrmApi.getTodayAttendance() });
  const employeeId = todayQ.data?.employeeId;

  const [cycleId, setCycleId] = useState(cycles[0]?.id ?? '');
  const [overallRating, setOverallRating] = useState(3);
  const [strengths, setStrengths] = useState('');
  const [improvements, setImprovements] = useState('');
  const [goalsForNextCycle, setGoalsForNextCycle] = useState('');
  const [comments, setComments] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.submitReview({
      cycleId, employeeId,
      reviewerType: 'self',
      overallRating,
      strengths: strengths || undefined,
      improvements: improvements || undefined,
      goalsForNextCycle: goalsForNextCycle || undefined,
      comments: comments || undefined,
    }),
    onSuccess: () => { toast.success('Self-review submitted'); onSubmitted(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · Review</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Submit self-review</h2>
        <p className="text-sm text-ink-500 mt-1">
          Reflect on the cycle honestly. Your manager will see this before their review.
        </p>
      </div>

      <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-4">
        <div>
          <label className="label">Cycle</label>
          <select className="input" value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
            {cycles.map((c: any) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
          </select>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label className="label mb-0">Overall rating</label>
            <span className="font-display font-bold text-xl text-ink-900 tabular-nums">
              {overallRating}<span className="text-sm text-ink-400">/5</span>
            </span>
          </div>
          <input type="range" min={1} max={5} step={1} className="w-full" value={overallRating}
            onChange={(e) => setOverallRating(parseInt(e.target.value))} />
          <div className="text-2xs text-ink-500 flex justify-between mt-1">
            <span>1 · Below</span>
            <span>3 · Meets</span>
            <span>5 · Outstanding</span>
          </div>
        </div>

        <div>
          <label className="label">Strengths</label>
          <textarea className="input" rows={2} value={strengths}
            onChange={(e) => setStrengths(e.target.value)}
            placeholder="What went well this cycle" />
        </div>
        <div>
          <label className="label">Areas to improve</label>
          <textarea className="input" rows={2} value={improvements}
            onChange={(e) => setImprovements(e.target.value)}
            placeholder="Honest self-assessment" />
        </div>
        <div>
          <label className="label">Goals for next cycle</label>
          <textarea className="input" rows={2} value={goalsForNextCycle}
            onChange={(e) => setGoalsForNextCycle(e.target.value)}
            placeholder="What you want to focus on" />
        </div>
        <div>
          <label className="label">Other comments <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={comments}
            onChange={(e) => setComments(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!cycleId || !employeeId || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Submitting…' : 'Submit review'}
        </button>
      </div>
    </Modal>
  );
}
